import { Button, EmptyState, PageHeader, ToastProvider, TooltipProvider } from '@provenance/design';
import { UserRole } from '@provenance/shared';
import { Compass } from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AppShell } from './components/AppShell';
import { ApiError } from './lib/api';
import { SessionProvider, useSession } from './lib/session';
import { ChamberDetail, ChamberReadiness, Chambers } from './routes/Chambers';
import { Dashboard } from './routes/Dashboard';
import { DocumentTypes } from './routes/DocumentTypes';
import { Email } from './routes/Email';
import { PlatformAdmins } from './routes/PlatformAdmins';
import { ExporterDetail, Exporters } from './routes/Exporters';
import { DocumentQueue } from './routes/DocumentQueue';
import { DocumentReview } from './routes/DocumentReview';
import { IntegratorDetail } from './routes/IntegratorDetail';
import { Integrators } from './routes/Integrators';
import { MasterData } from './routes/MasterData';
import { Modifications } from './routes/Modifications';
import { Numbering } from './routes/Numbering';
import { MySignaturePage, Operators } from './routes/Operators';
import { ChangePassword } from './routes/ChangePassword';
import { Mailbox } from './routes/Mailbox';
import { MailboxSettings } from './routes/MailboxSettings';
import { RegistrationDetail, Registrations } from './routes/Registrations';
import { RequiredDocuments } from './routes/RequiredDocuments';
import { Seals } from './routes/Seals';
import { SignIn } from './routes/SignIn';
import { SupportChambers } from './routes/SupportChambers';
import { SupportDesk, SupportTrail } from './routes/SupportDesk';
import { SupportIntegratorDetail, SupportIntegrators } from './routes/SupportIntegrators';
import { SetPassword } from './routes/SetPassword';
import { Stamping, StampingDetail } from './routes/Stamping';

/**
 * Query defaults.
 *
 * `retry` is the interesting one: a 4xx is the server telling us something definite —
 * this document is no longer actionable, this key is revoked, you are not authorised —
 * and retrying it three times just delays showing the operator a message they need to
 * read. Only 5xx and network failures are worth retrying.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <ToastProvider>
          <SessionProvider>
            <Router />
          </SessionProvider>
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function Router() {
  const { user, loading, landing, consumeLanding } = useSession();

  if (loading) {
    // A full-page spinner only for the initial session resolve, which is one request.
    // Everything after this point renders its own skeletons in place.
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="flex flex-col items-center gap-3">
          <span className="pv-seal-ring size-10 animate-pulse" />
          <p className="[font-size:13px] text-[var(--pv-muted)]">Loading Provenir…</p>
        </div>
      </div>
    );
  }

  // Signed out is not one screen. The invitation and password-reset links are followed by
  // people who have no account yet, or cannot get into the one they have — and returning
  // <SignIn /> for every path is exactly why the invitation link had never worked: the token
  // in the URL was discarded by a router that was not there.
  if (!user) {
    return (
      <Routes>
        <Route path="/activate" element={<SetPassword />} />
        <Route path="/reset" element={<SetPassword />} />
        <Route path="*" element={<SignIn />} />
      </Routes>
    );
  }

  // Just signed in: go to this person's landing page before rendering anything at the old URL
  // (T-140). Without this the sign-in screen's URL — often the previous user's — was kept.
  if (landing) return <Land to={landing} onDone={consumeLanding} />;

  const isPlatform = user.role === UserRole.PLATFORM_SUPER_ADMIN;
  const isChamberAdmin = user.role === UserRole.CHAMBER_ADMIN;
  // Provenir Support (D-031): works in one chosen chamber; until one is chosen, the picker.
  const isSupport = user.role === UserRole.PLATFORM_SUPPORT;
  const choosing = isSupport && !user.chamberId;
  // Every chamber screen, in one place. A Portal Admin has no chamber, so these answer 403;
  // only some of them used to redirect, and the rest rendered and failed (T-140).
  const chamberOnly = isPlatform ? (
    <Navigate to="/platform/integrators" replace />
  ) : choosing ? (
    <Navigate to="/support" replace />
  ) : (
    <Outlet />
  );

  return (
    <Routes>
      <Route element={<AppShell />}>
        {/* A platform admin has no chamber queue to land on, so their home is the
            chamber list rather than an empty dashboard. */}
        <Route
          index
          element={
            isPlatform ? (
              <Navigate to="/platform/integrators" replace />
            ) : choosing ? (
              <Navigate to="/support" replace />
            ) : (
              <Dashboard />
            )
          }
        />
        <Route path="support" element={isSupport ? <SupportChambers /> : <Navigate to="/" replace />} />
        {/* Find anything (T-145): support searches every chamber, so no chamber is needed yet. */}
        {/* Integrators (T-148): platform-level, so no chamber is needed. */}
        <Route path="support/integrators" element={isSupport ? <SupportIntegrators /> : <Navigate to="/" replace />} />
        <Route path="support/integrators/:id" element={isSupport ? <SupportIntegratorDetail /> : <Navigate to="/" replace />} />
        <Route path="support-desk" element={isSupport || isChamberAdmin ? <SupportDesk /> : <Navigate to="/" replace />} />

        <Route element={chamberOnly}>
          <Route path="support-desk/trail" element={isSupport || isChamberAdmin ? <SupportTrail /> : <Navigate to="/" replace />} />
          {/* Chamber users only. A platform admin has no chamber, so every request from
              these screens is scoped to nothing and answers 403 — and the same stale-cache
              reasoning applies in the other direction. */}
          <Route path="documents">
            <Route index element={<Navigate to="/documents/pending" replace />} />
            <Route path="pending" element={<DocumentQueue variant="pending" />} />
            <Route path="clarification" element={<DocumentQueue variant="clarification" />} />
            <Route path="processed" element={<DocumentQueue variant="processed" />} />
            <Route path=":id" element={<DocumentReview />} />
          </Route>

          {/* Email that reached the chamber and did not become a request (§26.4). */}
          {/* The Mailbox replaced Held email (T-136); held messages are its "Needs a look" view. */}
          <Route path="quarantine" element={<Navigate to="/mailbox?filter=held" replace />} />
          <Route path="mailbox" element={<Mailbox />} />
          <Route path="registrations">
            <Route index element={<Registrations />} />
            <Route path=":id" element={<RegistrationDetail />} />
          </Route>

          <Route path="modifications" element={<Modifications />} />

          {/* Readable by an operator too: reviewing a declaration means knowing whose
              specimen is on file and whether it is current. Only an admin can change one. */}
          <Route path="exporters">
            <Route index element={<Exporters />} />
            <Route path=":organisationId" element={<ExporterDetail />} />
          </Route>

          {/* Chamber Admin only. The API enforces this too — this stops the console
              offering a door that answers 403, and sends an operator somewhere useful
              instead of showing them an error they cannot act on. */}
          <Route
            path="configuration"
            element={isChamberAdmin || isSupport ? undefined : <Navigate to="/profile/signature" replace />}
          >
            <Route index element={<ChamberReadiness />} />
            <Route path="master-data" element={<MasterData />} />
            <Route path="seals" element={<Seals />} />
            <Route path="required-documents" element={<RequiredDocuments />} />
            <Route path="stamping">
              <Route index element={<Stamping />} />
              <Route path=":documentTypeId" element={<StampingDetail />} />
            </Route>
            <Route path="numbering" element={<Numbering />} />
            <Route path="users" element={<Operators />} />
            <Route path="mailbox" element={<MailboxSettings />} />
          </Route>
        </Route>

        {/* Platform Super Admin only.
            
            This guard was missing, and its absence was visible: a chamber user landing on
            /platform/chambers — by signing out and back in without the URL changing, or
            simply by typing it — rendered the page. The API refused every request it made
            (403), so no new data arrived, but anything already in the query cache was shown.
            
            A route that renders for the wrong role is a bug even when the API holds,
            because "the server will refuse it" says nothing about what is already on the
            client. See the note on signOut in lib/session.tsx. */}
        <Route
          path="platform"
          element={isPlatform ? undefined : <Navigate to="/" replace />}
        >
          <Route index element={<Navigate to="/platform/integrators" replace />} />
          <Route path="integrators" element={<Integrators />} />
          <Route path="integrators/:id" element={<IntegratorDetail />} />
          <Route path="document-types" element={<DocumentTypes />} />
          <Route path="email" element={<Email />} />
          <Route path="admins" element={<PlatformAdmins />} />
          <Route path="support-officers" element={<PlatformAdmins kind="support" />} />
          <Route path="chambers">
            <Route index element={<Chambers />} />
            <Route path=":id" element={<ChamberDetail />} />
          </Route>
        </Route>

        <Route path="profile">
          <Route path="signature" element={<MySignaturePage />} />
          <Route path="password" element={<ChangePassword />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** Sends a just-signed-in person to their landing page, once. */
function Land({ to, onDone }: { to: string; onDone: () => void }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace: true });
    onDone();
  }, [navigate, to, onDone]);
  return null;
}

/**
 * The console's 404.
 *
 * Says what the person needs and nothing about how the system works. It used to reuse the
 * development placeholder, which named task files and explained that cross-chamber records
 * answer 404 rather than 403 — accurate, useful to us, and exactly the sort of thing that
 * should not be shown to whoever mistyped a URL.
 *
 * Note what it does NOT say: whether the record exists. A missing record and one belonging
 * to another chamber look identical here, which is deliberate and is why the wording is
 * vague about the cause.
 */
function NotFound() {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Page not found" />
      <EmptyState
        icon={<Compass className="size-5" />}
        title="We could not find that page"
        description="The link may be out of date, or the page may have moved. Check the address, or go back to where you started."
        action={
          <Button variant="primary" asChild>
            <Link to="/">Back to the dashboard</Link>
          </Button>
        }
      />
    </div>
  );
}
